import { Trail } from "./Trail";
import { useFrame, useThree, extend } from "@react-three/fiber";
import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { MeshLineGeometry, MeshLineMaterial } from "meshline";

import { animated, useSpring, config, easings } from "@react-spring/three";
import { Billboard, Text } from "@react-three/drei";
import { duration } from "@mui/material";

const worldPos = /* @__PURE__ */ new THREE.Vector3();

const Cursor = ({ color = "#37a46e", show = true, text = "" }) => {
  const trailRef = useRef();
  const groupRef = useRef();
  const { camera, size } = useThree();

  const trailScaleRef = useRef(1);

  const [visible, setVisible] = useState(show);

  useFrame((state) => {
    const obj = groupRef.current;
    if (!obj) return;
    const sf = calculateScaleFactor(obj.getWorldPosition(worldPos), 4, state.camera, state.size);
    obj.scale.setScalar(sf * 3);

    // trailRef.current.material.uniforms.lineWidth.value = sf;
  }, 2);

  const getTrailWidth = (t) => {
    const obj = groupRef.current;
    if (!obj) return;
    const sf = calculateScaleFactor(obj.getWorldPosition(worldPos), 100, camera, size);

    return t * t * sf * trailScaleRef.current;
  };

  const showSpring = useSpring({
    scale: show ? 1 : 0,
    onChange: ({ value }) => {
      trailScaleRef.current = value.scale;
    },
    onStart: () => {
      if (show) setVisible(true);
    },
    onRest: () => {
      if (!show) setVisible(false);
    },

    config: { duration: 150, easing: easings.easeInOutQuad },
  });

  return (
    <animated.group scale={showSpring.scale} visible={visible}>
      <Trail
        width={4}
        // decay={1.000001}
        ref={trailRef}
        attenuation={getTrailWidth}
        layers={[0]}
        length={3}
        color={new THREE.Color(color).multiplyScalar(15)}
        render={visible}
      >
        <group layers={[2]} renderOrder={1} ref={groupRef}>
          <mesh userData={{ cannotReceiveAO: true }}>
            <sphereGeometry />
            <meshBasicMaterial color={new THREE.Color(color).multiplyScalar(15)} depthTest={false} transparent />
          </mesh>
        </group>
        {/* <meshLineMaterial
          color={new THREE.Color(color).multiplyScalar(15)}
          depthTest={false}
          transparent
        /> */}
      </Trail>
      <Billboard>
        <Text
          color={new THREE.Color(color)}
          fontSize={0.02}
          position={[0.04, 0, 0]}
          textAlign="left"
          anchorX="left"
          renderOrder={2}
          // outlineColor={"black"}
          // outlineWidth={0.001}
        >
          {text}
          <meshBasicMaterial depthTest={false} transparent />
        </Text>
      </Billboard>
    </animated.group>
  );
};

const tV0 = /* @__PURE__ */ new THREE.Vector3();
const tV1 = /* @__PURE__ */ new THREE.Vector3();
const tV2 = /* @__PURE__ */ new THREE.Vector3();

const getPoint2 = (point3, camera, size) => {
  const widthHalf = size.width / 2;
  const heightHalf = size.height / 2;
  camera.updateMatrixWorld(false);
  const vector = point3.project(camera);
  vector.x = vector.x * widthHalf + widthHalf;
  vector.y = -(vector.y * heightHalf) + heightHalf;
  return vector;
};

const getPoint3 = (point2, camera, size, zValue) => {
  const vector = tV0.set((point2.x / size.width) * 2 - 1, -(point2.y / size.height) * 2 + 1, zValue);
  vector.unproject(camera);
  return vector;
};

export const calculateScaleFactor = (point3, radiusPx, camera, size) => {
  const point2 = getPoint2(tV2.copy(point3), camera, size);
  let scale = 0;
  for (let i = 0; i < 2; ++i) {
    const point2off = tV1.copy(point2).setComponent(i, point2.getComponent(i) + radiusPx);
    const point3off = getPoint3(point2off, camera, size, point2off.z);
    scale = Math.max(scale, point3.distanceTo(point3off));
  }
  return scale;
};

export default Cursor;
